import { useMutation } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import type { CourseEquivalenceFormType } from '@/schemas/education/match-courses-schema'
import { DisciplineAnalysisService } from '@/services/education/discipline-analysis'
import { useEquivalenceStore } from '@/store/equivalence-store'
import type { Curriculum } from '@/types/courses/curriculum'
import type { DesiredEducationCourse } from '@/types/education/desired-education-course'
import type { PreviousEducationCourse } from '@/types/education/previous-education-course'
import type { StudentRequestedCoursesFormMethods } from '@/types/forms/student-requested-courses-form-methods'
import type { Request } from '@/types/requests/request'

interface UseCourseEquivalencesProps {
  curriculumCourses?: DesiredEducationCourse[]
  previousCourses?: PreviousEducationCourse[]
  formMethods?: StudentRequestedCoursesFormMethods
  currentCurriculum?: Curriculum
  request?: Request
  variant?: 'student' | 'manager'
}

export const useCourseEquivalence = ({
  curriculumCourses,
  previousCourses,
  formMethods,
  currentCurriculum,
  request
}: UseCourseEquivalencesProps) => {
  const [alreadyAnalyzed, setAlreadyAnalyzed] = useState(false)

  const {
    data: disciplineAnalysisData,
    isPending: isPendingDsiciplineAnalysis,
    mutate: mutateDisciplineAnalysis
  } = useMutation({
    mutationFn: () =>
      DisciplineAnalysisService(curriculumCourses, previousCourses)
  })

  const equivalenceStore = useEquivalenceStore.getState()

  function handleDisciplineAnalysis() {
    mutateDisciplineAnalysis()
  }

  function setEquivalences() {
    if (disciplineAnalysisData) {
      const equivalences: CourseEquivalenceFormType = []
      for (const equivalence of disciplineAnalysisData) {
        const previousDiscipline = previousCourses?.find(
          (course) => course.name === equivalence.previousDiscipline
        )
        const desiredDiscipline = curriculumCourses?.find((course) => {
          return course.name === equivalence.desiredDiscipline
        })

        if (previousDiscipline && desiredDiscipline) {
          equivalences.push({
            desiredCourse: {
              courseId: desiredDiscipline?.courseId,
              name: desiredDiscipline.name,
              workload: desiredDiscipline.workload,
              id: desiredDiscipline.id,
              period: desiredDiscipline.period,
              serie: String(desiredDiscipline.serie)
            },
            equivalenceStatus: {
              id: ''
            },
            previousCourse: {
              finalNumericGrade: previousDiscipline.finalNumericGrade,
              name: previousDiscipline.name,
              period: previousDiscipline.period,
              workload: previousDiscipline.workload,
              id: previousDiscipline.id
            },
            selectedByAI: false
          })
        }
      }

      formMethods?.setValue?.('courseEquivalences', equivalences)

      if (request && currentCurriculum) {
        const storedValue = equivalenceStore.getRecord(
          request.id,
          currentCurriculum.id
        )

        if (!storedValue) {
          equivalenceStore.addRecord({
            requestId: request.id,
            curriculumId: currentCurriculum.id,
            courseEquivalences: equivalences
          })
        }
      }
    }
  }

  const canEditDesiredCourse = () => {
    let isEditable = true

    if (!request?.desiredEducation && !currentCurriculum) isEditable = false

    return isEditable
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (disciplineAnalysisData) {
      setEquivalences()
    }
  }, [disciplineAnalysisData])

  useEffect(() => {
    const storeEquivalence = equivalenceStore.getRecord(
      request?.id || '',
      currentCurriculum?.id || ''
    )
    if (storeEquivalence !== undefined) {
      setAlreadyAnalyzed(true)
    } else {
      setAlreadyAnalyzed(false)
    }
  }, [equivalenceStore, currentCurriculum, request])

  return {
    states: {
      isPendingDsiciplineAnalysis,
      alreadyAnalyzed
    },
    actions: {
      handleDisciplineAnalysis,
      canEditDesiredCourse
    }
  }
}
